import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import StoreIcon from "@mui/icons-material/Store";
import { getBooths, createBooth } from '../../api/boothApi';
import { getTeleportTable } from '../../api/teleportApi';
import { signalRService } from '../../services/signalRService';
import { SIGNALR_EVENTS } from '../../constants/signalREvents';
import { CONTENT_TYPES } from '../../constants/contentTypes';
import BoothCard from "./BoothCard";
import CreateBoothModal from "./CreateBoothModal";
import EditBoothModal from "./EditBoothModal";
import { useLocalText } from "../common/useLocalText";
import LocalText from "../common/LocalText";
import { useToast } from "../../context/ToastContext";
import { useSpaceId } from "../../hooks/useSpaceId";
import StatusTabs from "../approval/StatusTabs";
import { buildVersionedList, filterVersionedList, countVersionedTabs } from "../../utils/versionedContent";
import { usePermissions } from "../../hooks/usePermissions";

function BoothGrid() {
  const spaceId  = useSpaceId();
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/booth', 'booth-edit');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, boothId: null, initialData: null, actionType: null, contentName: null, approvalMode: false });
  const [teleportButtons, setTeleportButtons] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tabValue, setTabValue] = useState('live');
  const [highlight, setHighlight] = useState({ tab: null, token: 0 });
  const flashTab = useCallback((tab) => setHighlight({ tab, token: Date.now() }), []);
  const searchPlaceholder = useLocalText("Booth Name");
  const timerRef = useRef(null);

  const loadBooths = useCallback(async () => {
    try {
      const data = await getBooths();
      setItems(data);
    } catch (err) {
      console.error('Failed to fetch booths:', err);
    }
  }, []);

  useEffect(() => {
    if (!spaceId) return;
    loadBooths();
  }, [spaceId, loadBooths]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // A map spot already tied to a standalone teleport button must not be pickable for a
  // booth either — fetch teleport buttons fresh whenever the create/edit modal opens.
  useEffect(() => {
    if (!open && !editModal.open) return;
    getTeleportTable()
      .then((res) => {
        const buttons = Array.isArray(res.buttons) ? res.buttons : res.data?.buttons ?? [];
        setTeleportButtons(buttons);
      })
      .catch(console.error);
  }, [open, editModal.open]);

  const usedMapSpotIds = useMemo(
    () => new Set([
      ...items.map((b) => b.mapSpotId),
      ...teleportButtons.map((b) => b.mapSpot?.id),
    ].filter(Boolean)),
    [items, teleportButtons]
  );

  // Editing a booth shouldn't disable the spot it already occupies.
  const editUsedMapSpotIds = useMemo(() => {
    const ownSpotId = editModal.initialData?.mapSpotId;
    if (ownSpotId == null || !usedMapSpotIds.has(ownSpotId)) return usedMapSpotIds;
    const next = new Set(usedMapSpotIds);
    next.delete(ownSpotId);
    return next;
  }, [usedMapSpotIds, editModal.initialData]);

  // Refresh booth list when an approval decision is made for any booth
  useEffect(() => {
    const handleDecision = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.BOOTH) loadBooths();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
  }, [loadBooths]);

  useEffect(() => {
    const handleWithdrawn = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.BOOTH) loadBooths();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
  }, [loadBooths]);

  const handleOnSave = async (data) => {
    await createBooth(data);
    await loadBooths();
    flashTab('draft');
    toast.success('Booth created and moved to Draft.');
  };

  const handleCardAction = useCallback((targetTab) => {
    loadBooths();
    if (targetTab) flashTab(targetTab);
  }, [loadBooths, flashTab]);

  const handleEditRejected = useCallback((snap, boothId, actionType, contentName) => {
    setEditModal({ open: true, boothId, initialData: snap, actionType, contentName, approvalMode: false });
  }, []);

  const handleEdit = useCallback((item, canDirectEdit) => {
    const name =
      item.localizedPairs?.values?.find((v) => v.localeId?.startsWith('en'))?.value ||
      item.localizedPairs?.values?.[0]?.value ||
      `Booth ${item.id}`;
    setEditModal({ open: true, boothId: item.id, initialData: item, actionType: null, contentName: name, approvalMode: !canDirectEdit });
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  };

  const versioned = useMemo(() => buildVersionedList(items), [items]);
  const counts    = useMemo(() => countVersionedTabs(items),  [items]);

  const filteredItems = useMemo(() => {
    let result = filterVersionedList(versioned, tabValue);
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return result;
    return result.filter((item) =>
      item.localizedPairs?.values?.some((v) => v.value?.toLowerCase().includes(q))
    );
  }, [versioned, tabValue, debouncedSearch]);

  return (
    <>
      <StatusTabs items={items} counts={counts} value={tabValue} onChange={setTabValue} highlightTab={highlight.tab} highlightToken={highlight.token} />

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          mb: 4,
        }}
      >
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "#85a5c6" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            maxWidth: { xs: '100%', sm: 320 },
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: "white",
              "& fieldset": { borderColor: "#dde6f0" },
              "&:hover fieldset": { borderColor: "#5383b3" },
              "&.Mui-focused fieldset": { borderColor: "#0a326d" },
            },
          }}
        />

        <Box display="flex" alignItems="center" gap={3}>
          <Typography
            variant="caption"
            sx={{
              color: "#85a5c6",
              fontWeight: 600,
              display: { xs: "none", sm: "block" },
            }}
          >
            {filteredItems.length} <LocalText>booths</LocalText>
          </Typography>

          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(e) => { e.currentTarget.blur(); setOpen(true); }}
              sx={{
                bgcolor: "#0a326d",
                color: "white",
                borderRadius: "12px",
                px: 2.5,
                py: 1,
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                whiteSpace: "nowrap",
                boxShadow: "0 4px 12px rgba(10,50,109,0.25)",
                "&:hover": { bgcolor: "#0d3d85", boxShadow: "0 6px 16px rgba(10,50,109,0.35)" },
              }}
            >
              <LocalText>Create Booth</LocalText>
            </Button>
          )}
        </Box>
      </Box>

      {/* Empty state */}
      {filteredItems.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <StoreIcon sx={{ fontSize: 52, color: "#c8d8ea", mb: 1.5 }} />
          <Typography variant="body1" sx={{ color: "#85a5c6", fontWeight: 500 }}>
            {debouncedSearch
              ? <LocalText>No booths match your search</LocalText>
              : <LocalText>No booths yet. Create one!</LocalText>
            }
          </Typography>
        </Box>
      )}

      {/* Grid — responsive columns */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(1, minmax(0, 1fr))", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" }, gap: 2 }}>
        {filteredItems.map((item) => (
          <BoothCard
            key={item._viewType === 'snapshot' ? `snap-${item._liveId}` : item.id}
            item={item}
            onSuccess={handleCardAction}
            onEditRejected={handleEditRejected}
            onEdit={handleEdit}
          />
        ))}
      </Box>

      <CreateBoothModal
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleOnSave}
        usedMapSpotIds={usedMapSpotIds}
      />

      <EditBoothModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, boothId: null, initialData: null, actionType: null, contentName: null, approvalMode: false })}
        boothId={editModal.boothId}
        initialData={editModal.initialData}
        actionType={editModal.actionType}
        contentName={editModal.contentName}
        approvalMode={editModal.approvalMode}
        usedMapSpotIds={editUsedMapSpotIds}
        onSaved={(targetTab) => {
          setEditModal({ open: false, boothId: null, initialData: null, actionType: null, contentName: null, approvalMode: false });
          loadBooths();
          if (targetTab) flashTab(targetTab);
        }}
      />
    </>
  );
}

export default BoothGrid;
